import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {compose} from 'redux';
import styled from 'styled-components';

import {
  postManualPayment,
  postOrderCheckSbpRefundStatus,
  postOrderChangeOwner,
  postOrderRefundParking,
  postOrderRefundSbpOverpayment,
} from '../../api';
import getOrderDetailsAction from '../../redux/actions/getOrderDetails';
import * as colors from '../../util/colors';
import AdminUserPicker, {
  formatAdminUserAmount,
  formatAdminUserName,
} from '../common/AdminUserPicker';
import {ErrorAlert} from '../common/Alerts';
import Button from '../common/Button';
import Paper from '../common/Paper';
import TitleField from '../common/TitleField';
import {MetersFormatted, PreLog} from './OrderHistory';

const getErrorMessage = async (response, fallback) => {
  try {
    const data = await response.json();
    return data.error || fallback;
  } catch (e) {
    return fallback;
  }
};

const getOrderTotalAmount = (order) => Number(order.charging_price || 0) + Number(order.reservation_price || 0);

const getDefaultManualAmount = (order) => {
  const amount = getOrderTotalAmount(order) - Number(order.captured_amount || 0);

  return Math.max(Math.round(amount), 0);
};

const getParkingRefundAmount = (order) => Number(order.parking_amount || 0);

const getIsSbpRefundPending = refund => Boolean(refund && refund.is_status_pending); // eslint-disable-line camelcase

const formatKopeksLabel = (value) => {
  const amount = Number(value || 0);
  const rubles = Math.floor(amount / 100);
  const kopeks = amount % 100;

  return `${rubles} р. ${String(kopeks).padStart(2, '0')} коп.`;
};

const buildLimits = (order) => {
  const limits = [];

  if (order.limit_amount) { limits.push(`${order.limit_amount / 100} р.`); }
  if (order.limit_kwh) { limits.push(`${order.limit_kwh} кВтч`); }
  if (order.limit_percents) { limits.push(`${order.limit_percents} %`); }

  return limits.length > 0 ? limits.join(', ') : '—';
};

function OrderActionsTab(props) {
  const {
    authorization,
    orderId,
    data,
    getOrderDetails: loadOrderDetails,
  } = props;

  const order = data?.order;
  const sbpRefund = data?.sbp_refund; // eslint-disable-line camelcase
  const {
    captured_amount: orderCapturedAmount,
    charging_price: orderChargingPrice,
    reservation_price: orderReservationPrice,
  } = order || {};

  const [selectedUser, setSelectedUser] = React.useState(null);
  const [manualAmount, setManualAmount] = React.useState('');
  const [changeOwnerLoading, setChangeOwnerLoading] = React.useState(false);
  const [changeOwnerError, setChangeOwnerError] = React.useState('');
  const [changeOwnerSuccess, setChangeOwnerSuccess] = React.useState('');
  const [manualPaymentLoading, setManualPaymentLoading] = React.useState(false);
  const [manualPaymentError, setManualPaymentError] = React.useState('');
  const [manualPaymentSuccess, setManualPaymentSuccess] = React.useState('');
  const [parkingRefundLoading, setParkingRefundLoading] = React.useState(false);
  const [parkingRefundError, setParkingRefundError] = React.useState('');
  const [parkingRefundSuccess, setParkingRefundSuccess] = React.useState('');
  const [sbpRefundLoading, setSbpRefundLoading] = React.useState(false);
  const [sbpStatusCheckLoading, setSbpStatusCheckLoading] = React.useState(false);
  const [sbpRefundError, setSbpRefundError] = React.useState('');
  const [sbpRefundSuccess, setSbpRefundSuccess] = React.useState('');
  const [sbpRefundResult, setSbpRefundResult] = React.useState(null);

  React.useEffect(() => {
    loadOrderDetails(authorization, orderId);
  }, [authorization, loadOrderDetails, orderId]);

  React.useEffect(() => {
    setSbpRefundResult(null);
    setSbpRefundError('');
    setSbpRefundSuccess('');
  }, [orderId]);

  React.useEffect(() => {
    if (!order?.id) {
      return;
    }

    setManualAmount(String(getDefaultManualAmount(order)));
  }, [order, orderCapturedAmount, orderChargingPrice, orderReservationPrice]);

  if (!order?.id) {
    return null;
  }

  const manualAmountNumber = Number(manualAmount || 0);
  const isManualAmountValid = Number.isInteger(manualAmountNumber) && manualAmountNumber > 0;
  const isAccountPayment = order.payment_method_type === 'account';
  const canChangeOwner = order.payment_method_type === 'cash';
  const parkingRefundAmount = getParkingRefundAmount(order);
  const hasPaidParking = Boolean(
    order.parking_payment_id
    && parkingRefundAmount > 0
    && Number(order.parking_captured_amount || 0) > 0
  );
  const canRefundParking = hasPaidParking && isAccountPayment;
  const isSbpPayment = order.payment_method_type === 'sbp';
  const sbpRefundAmount = Math.max(
    Number(order.captured_amount || 0) - Number(order.charging_price || 0),
    0
  );
  const currentSbpRefund = sbpRefundResult || sbpRefund;
  const isSbpRefundPending = getIsSbpRefundPending(currentSbpRefund);
  const hasSbpRefund = Boolean(currentSbpRefund);
  const canRefundSbpOverpayment = isSbpPayment && sbpRefundAmount > 0 && !isSbpRefundPending;
  const manualPaymentButtonLabel = isManualAmountValid
    ? `Списать ${formatKopeksLabel(manualAmountNumber)} с личного счета пользователя`
    : 'Списать с личного счета пользователя';
  const parkingRefundButtonLabel = hasPaidParking
    ? `Вернуть ${formatKopeksLabel(parkingRefundAmount)} на личный счет пользователя`
    : 'Вернуть средства за платный простой';
  let sbpRefundButtonLabel = 'Нет переплаты для возврата';
  if (sbpRefundAmount > 0) {
    sbpRefundButtonLabel = `Вернуть ${formatKopeksLabel(sbpRefundAmount)} через СБП`;
  }
  if (isSbpRefundPending) {
    sbpRefundButtonLabel = 'Возврат обрабатывается';
  }

  const handleChangeOwner = async () => {
    if (!selectedUser) {
      setChangeOwnerError('Сначала выберите пользователя');
      setChangeOwnerSuccess('');
      return;
    }

    setChangeOwnerLoading(true);
    setChangeOwnerError('');
    setChangeOwnerSuccess('');

    try {
      const response = await postOrderChangeOwner(order.id, {authorization}, {
        new_owner_id: selectedUser.id,
      });

      if ((response.status < 200) || (response.status >= 300)) {
        throw new Error(await getErrorMessage(response, 'Не удалось перекинуть сессию на пользователя'));
      }

      setChangeOwnerSuccess(`Сессия перенесена на пользователя ${formatAdminUserName(selectedUser)}.`);
      setSelectedUser(null);
      loadOrderDetails(authorization, orderId);
    } catch (error) {
      setChangeOwnerError(error.message || 'Не удалось перекинуть сессию на пользователя');
    } finally {
      setChangeOwnerLoading(false);
    }
  };

  const handleManualPayment = async () => {
    if (!order.user_id) {
      setManualPaymentError('У сессии отсутствует user_id');
      setManualPaymentSuccess('');
      return;
    }

    if (!isManualAmountValid) {
      setManualPaymentError('Укажите корректную сумму в копейках');
      setManualPaymentSuccess('');
      return;
    }

    setManualPaymentLoading(true);
    setManualPaymentError('');
    setManualPaymentSuccess('');

    try {
      const response = await postManualPayment({authorization}, {
        amount: manualAmountNumber,
        order_id: order.id,
        user_id: order.user_id,
        provider: 'tinkoff',
      });

      if ((response.status < 200) || (response.status >= 300)) {
        throw new Error(await getErrorMessage(response, 'Не удалось списать средства за зарядную сессию'));
      }

      const payment = await response.json();
      const paymentStatus = payment.status || 'captured';
      const paymentId = payment.id ? ` ID ${payment.id}.` : '';

      setManualPaymentSuccess(`Списание выполнено: ${formatKopeksLabel(manualAmountNumber)}, статус ${paymentStatus}.${paymentId}`);
      loadOrderDetails(authorization, orderId);
    } catch (error) {
      setManualPaymentError(error.message || 'Не удалось списать средства за зарядную сессию');
    } finally {
      setManualPaymentLoading(false);
    }
  };

  const handleParkingRefund = async () => {
    if (!hasPaidParking) {
      setParkingRefundError('У заказа нет оплаченного платного простоя для возврата');
      setParkingRefundSuccess('');
      return;
    }

    if (!isAccountPayment) {
      setParkingRefundError('Возврат платного простоя сейчас поддержан только для сессий с типом оплаты account');
      setParkingRefundSuccess('');
      return;
    }

    // eslint-disable-next-line no-alert
    const isConfirmed = window.confirm([
      `Вернуть ${formatKopeksLabel(parkingRefundAmount)} на личный счет пользователя?`,
      'Возвращается полный parking_amount, даже если в payments.amount было списано меньше.',
    ].join('\n'));
    if (!isConfirmed) {
      return;
    }

    setParkingRefundLoading(true);
    setParkingRefundError('');
    setParkingRefundSuccess('');

    try {
      const response = await postOrderRefundParking(order.id, {authorization});

      if ((response.status < 200) || (response.status >= 300)) {
        throw new Error(await getErrorMessage(response, 'Не удалось выполнить возврат средств за платный простой'));
      }

      await response.json();

      setParkingRefundSuccess(`Возврат выполнен: ${formatKopeksLabel(parkingRefundAmount)} возвращено на личный счет пользователя.`);
      loadOrderDetails(authorization, orderId);
    } catch (error) {
      setParkingRefundError(error.message || 'Не удалось выполнить возврат средств за платный простой');
    } finally {
      setParkingRefundLoading(false);
    }
  };

  const handleSbpRefund = async () => {
    if (!canRefundSbpOverpayment) {
      setSbpRefundError('В заказе нет переплаты для частичного возврата');
      setSbpRefundSuccess('');
      return;
    }

    // eslint-disable-next-line no-alert
    const isConfirmed = window.confirm([
      `Вернуть клиенту ${formatKopeksLabel(sbpRefundAmount)} через СБП?`,
      `После возврата оплаченная сумма составит ${formatKopeksLabel(order.charging_price)}.`,
    ].join('\n'));
    if (!isConfirmed) {
      return;
    }

    setSbpRefundLoading(true);
    setSbpRefundError('');
    setSbpRefundSuccess('');

    try {
      const response = await postOrderRefundSbpOverpayment(order.id, {authorization});

      if ((response.status < 200) || (response.status >= 300)) {
        throw new Error(await getErrorMessage(response, 'Не удалось выполнить частичный возврат через СБП'));
      }

      const result = await response.json();
      setSbpRefundResult(result);
      setSbpRefundSuccess(getIsSbpRefundPending(result)
        ? ''
        : `Возврат выполнен: ${formatKopeksLabel(result.refund_amount)}. Статус ${result.provider_status}.`);
      loadOrderDetails(authorization, orderId);
    } catch (error) {
      setSbpRefundError(error.message || 'Не удалось выполнить частичный возврат через СБП');
    } finally {
      setSbpRefundLoading(false);
    }
  };

  const handleSbpStatusCheck = async () => {
    setSbpStatusCheckLoading(true);
    setSbpRefundError('');
    setSbpRefundSuccess('');

    try {
      const response = await postOrderCheckSbpRefundStatus(order.id, {authorization});

      if ((response.status < 200) || (response.status >= 300)) {
        throw new Error(await getErrorMessage(response, 'Не удалось проверить статус возврата'));
      }

      const result = await response.json();
      setSbpRefundResult(result);
      setSbpRefundSuccess(getIsSbpRefundPending(result)
        ? ''
        : `Статус возврата обновлен: ${result.provider_status}.`);
      loadOrderDetails(authorization, orderId);
    } catch (error) {
      setSbpRefundError(error.message || 'Не удалось проверить статус возврата');
    } finally {
      setSbpStatusCheckLoading(false);
    }
  };

  return (
    <>
      <Paper>
        <FormSection>
          <SectionTitle>Перекинуть зарядную сессию на пользователя</SectionTitle>

          {!canChangeOwner && (
            <InfoBlock warning>
              Текущий тип оплаты: <Code>{order.payment_method_type || '—'}</Code>. Разрешен
              перенос только для сессий с типом <Code>cash</Code>.
            </InfoBlock>
          )}

          {changeOwnerError && (
            <AlertWrap>
              <ErrorAlert noTriangle>{changeOwnerError}</ErrorAlert>
            </AlertWrap>
          )}

          {changeOwnerSuccess && <SuccessAlert>{changeOwnerSuccess}</SuccessAlert>}

          <AdminUserPicker
            authorization={authorization}
            selectedUser={selectedUser}
            onSelect={(user) => {
              setSelectedUser(user);
              setChangeOwnerError('');
              setChangeOwnerSuccess('');
            }}
            emptyDescription="Выберите нового владельца сессии по телефону, имени, фамилии или email."
          />

          <ButtonRow>
            <Button
              disabled={changeOwnerLoading || !selectedUser || !canChangeOwner}
              margin="0"
              onClick={handleChangeOwner}
            >
              {changeOwnerLoading ? 'Перекидываем...' : 'Перекинуть сессию'}
            </Button>
          </ButtonRow>
        </FormSection>
      </Paper>

      {isSbpPayment && (
        <Paper>
          <FormSection>
            <SectionTitle>Частичный возврат клиенту за зарядную сессию</SectionTitle>
            <SectionDescription>
              Сумма возврата рассчитывается как разница между оплаченной суммой и стоимостью зарядки.
            </SectionDescription>

            <SbpRefundSummary
              order={order}
              refundAmount={sbpRefundAmount}
              refundState={currentSbpRefund}
            />

            {sbpRefundAmount <= 0 && !isSbpRefundPending && (
              <InfoBlock>
                Переплаты для возврата нет: оплаченная сумма не превышает стоимость зарядки.
              </InfoBlock>
            )}

            {isSbpRefundPending && (
              <InfoBlock warning>
                Провайдер по-прежнему возвращает статус <Code>ASYNC_REFUNDING</Code>. Локальные
                суммы в платеже и заказе уже уменьшены. Проверьте статус возврата позднее.
              </InfoBlock>
            )}

            {sbpRefundError && (
              <AlertWrap>
                <ErrorAlert noTriangle>{sbpRefundError}</ErrorAlert>
              </AlertWrap>
            )}

            {sbpRefundSuccess && <SuccessAlert>{sbpRefundSuccess}</SuccessAlert>}

            <ButtonRow>
              <Button
                disabled={sbpRefundLoading || sbpStatusCheckLoading || !canRefundSbpOverpayment}
                margin="0"
                onClick={handleSbpRefund}
              >
                {sbpRefundLoading
                  ? 'Возвращаем и проверяем статус...'
                  : sbpRefundButtonLabel}
              </Button>

              {hasSbpRefund && (
                <Button
                  disabled={sbpRefundLoading || sbpStatusCheckLoading}
                  margin="0"
                  onClick={handleSbpStatusCheck}
                >
                  {sbpStatusCheckLoading ? 'Проверяем статус...' : 'Проверить статус возврата'}
                </Button>
              )}
            </ButtonRow>
          </FormSection>
        </Paper>
      )}

      <Paper>
        <FormSection>
          <SectionTitle>Ручное списание с личного счета за зарядную сессию</SectionTitle>
          <SessionSummary order={order} />

          {!isAccountPayment && (
            <InfoBlock warning>
              Тип оплаты сессии сейчас <Code>{order.payment_method_type || '—'}</Code>. Этот запрос
              использует текущий тип оплаты заказа, поэтому для списания именно с личного счета нужен
              тип <Code>account</Code>.
            </InfoBlock>
          )}

          {manualPaymentError && (
            <AlertWrap>
              <ErrorAlert noTriangle>{manualPaymentError}</ErrorAlert>
            </AlertWrap>
          )}

          {manualPaymentSuccess && <SuccessAlert>{manualPaymentSuccess}</SuccessAlert>}

          <FieldGroup>
            <FieldLabel htmlFor="manual-payment-amount">Стоимость, коп.</FieldLabel>
            <FieldInput
              id="manual-payment-amount"
              type="text"
              inputMode="numeric"
              value={manualAmount}
              placeholder="Например, 36218"
              onChange={(event) => {
                setManualAmount(event.target.value.replace(/[^\d]/g, ''));
                setManualPaymentError('');
                setManualPaymentSuccess('');
              }}
            />
            <HelperText>
              {isManualAmountValid
                ? `${manualAmount} коп. = ${formatAdminUserAmount(manualAmountNumber)} ₽`
                : 'Укажите сумму в копейках'}
            </HelperText>
          </FieldGroup>

          <ButtonRow>
            <Button
              disabled={manualPaymentLoading || !isManualAmountValid || !isAccountPayment}
              margin="0"
              onClick={handleManualPayment}
            >
              {manualPaymentLoading ? 'Списываем...' : manualPaymentButtonLabel}
            </Button>
          </ButtonRow>
        </FormSection>
      </Paper>

      <Paper>
        <FormSection>
          <SectionTitle>Возврат средств за платный простой</SectionTitle>
          <SectionDescription>
            Возврат идет на личный счет пользователя в размере полного <Code>parking_amount</Code>(даже если
            в <Code>payments.amount</Code> было списано меньше).
          </SectionDescription>

          <ParkingRefundSummary order={order} />

          {!hasPaidParking && (
            <InfoBlock warning>
              Для этого заказа нет оплаченного платного простоя. Действие становится доступно,
              когда у сессии есть <Code>parking_payment_id</Code> и ненулевой
              <Code>parking_captured_amount</Code>.
            </InfoBlock>
          )}

          {hasPaidParking && !isAccountPayment && (
            <InfoBlock warning>
              Тип оплаты сессии сейчас <Code>{order.payment_method_type || '—'}</Code>. Этот
              возврат опирается на историю личного счета пользователя и сейчас рассчитан на
              сценарий с типом оплаты <Code>account</Code>.
            </InfoBlock>
          )}

          {parkingRefundError && (
            <AlertWrap>
              <ErrorAlert noTriangle>{parkingRefundError}</ErrorAlert>
            </AlertWrap>
          )}

          {parkingRefundSuccess && <SuccessAlert>{parkingRefundSuccess}</SuccessAlert>}

          <ButtonRow>
            <Button
              disabled={parkingRefundLoading || !canRefundParking}
              margin="0"
              onClick={handleParkingRefund}
            >
              {parkingRefundLoading ? 'Возвращаем...' : parkingRefundButtonLabel}
            </Button>
          </ButtonRow>
        </FormSection>
      </Paper>
    </>
  );
}

const SessionSummary = ({order}) => {
  const user = order.user || {};
  const userName = [user.first_name, user.last_name].filter(Boolean).join(' ');
  const userLine = [user.phone, userName, user.email].filter(Boolean).join(', ');

  return (
    <SummaryBlock>
      <TitleField>User</TitleField>{order.user_id}<br />
      <TitleField /><span style={{fontSize: '14px'}}>{userLine || '—'}</span><br />
      <TitleField>Тип оплаты</TitleField>{order.payment_method_type || '—'}<br />
      <TitleField>Тариф</TitleField>{order.rate / 100} / {order.tariff}
      {order.discount && order.discount > 0 && (<DiscountBadge>скидка {order.discount}%</DiscountBadge>)}<br />
      <TitleField>Лимиты</TitleField>{buildLimits(order)}<br />
      <TitleField>Статус предоплаты</TitleField><PaidStatus status={order.payment_intent_status}>{order.payment_intent_status}</PaidStatus><br />
      <TitleField>Оплачено</TitleField><PaidAmount order={order} /><br />
      <TitleField>Промежуточная сумма</TitleField>{formatAdminUserAmount(order.current_charging_amount)} р.<br />
      <TitleField>Последние метрики</TitleField><MetersFormatted meters={order.meters} /><br />
      <TitleField>Начальные метрики</TitleField><MetersFormatted meters={order.meters_begin} /><br />
      <TitleField>Длительность заряда</TitleField>{order.duration || '—'}<br />
      <TitleField>Причина остановки</TitleField><PreLog>{order.stop_reason || '—'}</PreLog><br />
      <br />
      <TitleField>Транзакция</TitleField><PreLog>{order.transaction_id || '—'}</PreLog>
      {(order.transaction_start || order.transaction_finish) ? ` (${order.transaction_start || '—'} - ${order.transaction_finish || '—'})` : ''}<br />
      <TitleField>Счетчик</TitleField><MeterCounter order={order} /><br />
    </SummaryBlock>
  );
};

OrderActionsTab.propTypes = {
  authorization: PropTypes.string,
  orderId: PropTypes.string,
  data: PropTypes.shape({
    order: PropTypes.shape({
      id: PropTypes.string,
    }),
    sbp_refund: PropTypes.shape({}), // eslint-disable-line camelcase
  }),
  getOrderDetails: PropTypes.func.isRequired,
};

OrderActionsTab.defaultProps = {
  authorization: '',
  orderId: '',
  data: null,
};

SessionSummary.propTypes = {
  order: PropTypes.shape({
    user: PropTypes.shape({
      phone: PropTypes.string,
      first_name: PropTypes.string,
      last_name: PropTypes.string,
      email: PropTypes.string,
    }),
    user_id: PropTypes.string,
    payment_method_type: PropTypes.string,
    rate: PropTypes.number,
    tariff: PropTypes.string,
    discount: PropTypes.number,
    payment_intent_status: PropTypes.string,
    current_charging_amount: PropTypes.number,
    meters: PropTypes.shape({}),
    meters_begin: PropTypes.shape({}),
    duration: PropTypes.string,
    stop_reason: PropTypes.string,
    transaction_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    transaction_start: PropTypes.string,
    transaction_finish: PropTypes.string,
    meter_start: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    meter_stop: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    meter_start_offset: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
};

const ParkingRefundSummary = ({order}) => (
  <SummaryBlock>
    <TitleField>Оплачено за простой</TitleField>
    {formatAdminUserAmount(order.parking_captured_amount)} р. из {formatAdminUserAmount(order.parking_amount)} р.<br />
    <TitleField>Возвращаем на счет</TitleField>
    {formatAdminUserAmount(order.parking_amount)} р.<br />
    <TitleField>parking_payment_id</TitleField>
    <PreLog>{order.parking_payment_id || '—'}</PreLog><br />
  </SummaryBlock>
);

ParkingRefundSummary.propTypes = {
  order: PropTypes.shape({
    parking_amount: PropTypes.number,
    parking_captured_amount: PropTypes.number,
    parking_payment_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
};

const SbpRefundSummary = ({order, refundAmount, refundState}) => (
  <SummaryBlock>
    <TitleField>Оплачено сейчас</TitleField>
    {formatAdminUserAmount(order.captured_amount)} р.<br />
    <TitleField>Стоимость зарядки</TitleField>
    {formatAdminUserAmount(order.charging_price)} р.<br />
    <TitleField>Доступно к возврату</TitleField>
    {formatAdminUserAmount(refundAmount)} р.<br />
    <TitleField>После возврата</TitleField>
    {formatAdminUserAmount(order.captured_amount - refundAmount)} р.<br />
    {refundState && (
      <>
        <TitleField>Всего возвращено</TitleField>
        {formatAdminUserAmount(refundState.refund_amount || refundState.refunded_amount)} р.<br />
        <TitleField>Статус возврата</TitleField>
        <PreLog>{refundState.provider_status || '—'}</PreLog><br />
      </>
    )}
  </SummaryBlock>
);

SbpRefundSummary.propTypes = {
  order: PropTypes.shape({
    captured_amount: PropTypes.number,
    charging_price: PropTypes.number,
  }).isRequired,
  refundAmount: PropTypes.number.isRequired,
  refundState: PropTypes.shape({
    provider_status: PropTypes.string,
    refund_amount: PropTypes.number,
    refunded_amount: PropTypes.number,
  }),
};

SbpRefundSummary.defaultProps = {
  refundState: null,
};

const MeterCounter = ({order}) => (
  <>
    <PreLog>{order.meter_start}</PreLog> - <PreLog>{order.meter_stop}</PreLog>
    {order.meter_start && order.meter_stop ? ` (${(order.meter_stop - order.meter_start) / 1000} кВтч)` : ''}
    {order.meter_start_offset ? ` (задвоение!, + ${order.meter_start_offset} )` : ''}
  </>
);

MeterCounter.propTypes = {
  order: PropTypes.shape({
    meter_start: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    meter_stop: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    meter_start_offset: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
};

const PaidAmount = ({order}) => {
  const cashbackAmount = order.cashback_amount > 0 ? `, кешбэк ${order.cashback_amount / 100} р.` : '';
  let kWh = (order.captured_amount / order.rate).toFixed(3);

  if (order.reservation_price) {
    kWh = ((order.captured_amount - order.reservation_price) / order.rate).toFixed(3);
    return (
      <>
        {order.captured_amount / 100} р. из {(order.charging_price + order.reservation_price) / 100} р.
        <span style={{color: '#AAA'}}>{` (${kWh} кВтч${cashbackAmount} + ${order.reservation_price / 100} р. бронь)`}</span>
      </>
    );
  }

  return (
    <>
      {order.captured_amount / 100} р. из {order.charging_price / 100} р.
      <span style={{color: '#AAA'}}>{` (${kWh} кВтч${cashbackAmount})`}</span>
    </>
  );
};

PaidAmount.propTypes = {
  order: PropTypes.shape({
    cashback_amount: PropTypes.number,
    captured_amount: PropTypes.number,
    rate: PropTypes.number,
    reservation_price: PropTypes.number,
    charging_price: PropTypes.number,
  }).isRequired,
};

const paidColorByValue = (status) => {
  switch (status) {
    case 'canceled':
      return colors.ICON_GRAY;
    case 'captured':
      return colors.ICON_GREEN;
    case 'created':
      return colors.ICON_ORANGE;
    default:
      return colors.ERROR;
  }
};

const PaidStatus = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 55px;
  font-weight: bold;
  border-radius: 12px;
  color: #fff;
  padding: 5px;
  height: 20px;
  font-size: 13px;
  background-color: ${props => paidColorByValue(props.status)};
`;

const DiscountBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 55px;
  font-weight: bold;
  border-radius: 12px;
  color: #fff;
  padding: 5px;
  height: 20px;
  font-size: 13px;
  margin-left: 5px;
  background-color: ${colors.ICON_ORANGE};
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  line-height: 1.2;
  color: ${colors.TEXT_TITLE};
`;

const SectionDescription = styled.div`
  margin-top: -8px;
  font-size: 14px;
  line-height: 1.6;
  color: ${colors.TEXT_MINOR};
`;

const Code = styled.span`
  padding: 2px 6px;
  border-radius: 6px;
  background: ${colors.BG};
  color: ${colors.TEXT_MAIN};
  font-family: monospace;
  font-size: 13px;
`;

const InfoBlock = styled.div`
  padding: 14px 16px;
  border: 1px solid ${props => props.warning ? '#F0D9A7' : colors.LINE};
  border-radius: 8px;
  background: ${props => props.warning ? '#FFF7E8' : colors.BG};
  color: ${colors.TEXT_MAIN};
  font-size: 14px;
  line-height: 1.6;
`;

const AlertWrap = styled.div`
  max-width: 100%;
`;

const SuccessAlert = styled.div`
  padding: 14px 16px;
  border: 1px solid #D8ECCA;
  border-radius: 8px;
  background: #F4FBEE;
  color: ${colors.GREEN_DARK};
  font-size: 14px;
  line-height: 1.5;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: flex-start;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FieldLabel = styled.label`
  font-size: 14px;
  color: ${colors.TEXT_MAIN};
  font-weight: bold;
`;

const FieldInput = styled.input`
  width: 100%;
  min-height: 44px;
  padding: 0 14px;
  border: 1px solid ${colors.INPUT_STROKE};
  border-radius: 8px;
  outline: none;
  font-size: 15px;
  color: ${colors.TEXT_MAIN};
  background: white;

  &:focus {
    border-color: ${colors.BUTTON_BLUE};
    box-shadow: 0 0 0 3px rgba(71, 146, 255, 0.12);
  }
`;

const HelperText = styled.div`
  font-size: 13px;
  line-height: 1.5;
  color: ${colors.TEXT_MINOR};
`;

const SummaryBlock = styled.div`
  padding: 20px 0 4px;
  border-top: 1px solid ${colors.LINE};
  border-bottom: 1px solid ${colors.LINE};
`;

const mapStateToProps = ({manager, orderDetails}, {match}) => ({
  authorization: manager.accessToken && `Bearer ${manager.accessToken}`,
  orderId: match.params.orderId,
  data: orderDetails.data,
});

const mapDispatchToProps = dispatch => ({
  getOrderDetails: (...params) => dispatch(getOrderDetailsAction(...params)),
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
)(OrderActionsTab);
